import { readSyllable } from "@-/com.npmjs.kensio__pinyinjs";

/**
 * The pinyin finals as a chart: each row is a base final (`a`, `an`, …) followed by its `i`-, `u`-
 * and `ü`-series forms, `null` where the series has none. The bare `i`, `u` and `ü` follow, then
 * `er` on its own row. Finals are written in full (`iou`, `uei`, `uen`), not in the spelling's
 * short forms.
 */
export const PINYIN_FINAL_ROWS = [
  ["a", "ia", "ua", null],
  ["o", null, "uo", null],
  ["e", "ie", null, "üe"],
  ["ai", null, "uai", null],
  ["ei", null, "uei", null],
  ["ao", "iao", null, null],
  ["ou", "iou", null, null],
  ["an", "ian", "uan", "üan"],
  ["en", "in", "uen", "ün"],
  ["ang", "iang", "uang", null],
  ["eng", "ing", "ueng", null],
  ["ong", "iong", null, null],
  [null, "i", "u", "ü"],
  ["er", null, null, null],
] as const;

export type PinyinFinal = NonNullable<(typeof PINYIN_FINAL_ROWS)[number][number]>;

export const PINYIN_FINALS: readonly PinyinFinal[] = PINYIN_FINAL_ROWS.flat().filter(
  (final): final is PinyinFinal => final !== null,
);

const PINYIN_FINAL_SET: ReadonlySet<string> = new Set(PINYIN_FINALS);

function isPinyinFinal(value: string): value is PinyinFinal {
  return PINYIN_FINAL_SET.has(value);
}

/** The curriculum charts omit the apical vowel of 是 and 字. */
const APICAL_I_INITIALS: ReadonlySet<string> = new Set(["zh", "ch", "sh", "r", "z", "c", "s"]);

/**
 * The chart final of a tone-number syllable (`kuai4` → `uai`, `you3` → `iou`, `jue2` → `üe`, `nv3`
 * → `ü`, `er2` → `er`), or `null` for one the chart has no cell for: the buzzed `-i` of 是 shì
 * and 字 zì, and interjections like `hm`.
 */
export function getPinyinFinal(toneNumbersPinyin: string): PinyinFinal | null {
  const syllable = readSyllable(toneNumbersPinyin);
  if (
    syllable === undefined ||
    (syllable.final === "i" && APICAL_I_INITIALS.has(syllable.initial))
  ) {
    return null;
  }
  return isPinyinFinal(syllable.final) ? syllable.final : null;
}
